// ─────────────────────────────────────────────────────────────────────────────
// Mock Clinical Intelligence Data — Phase 4
// Used when USE_MOCK = true for offline development/testing
// ─────────────────────────────────────────────────────────────────────────────

// ─── Patient Progress Scoring ────────────────────────────────────────────────

export const mockPatientProgress = {
  rescode: 1,
  message: 'Success',
  data: {
    patients: [
      {
        patient_id: 101,
        patient_name: 'Aryan Sharma',
        progress_score: 72.5,
        progress_level: 'Improving',
        monthly_progress: [
          { month: '2025-10', total: 8, completed: 5, rate: 62.5 },
          { month: '2025-11', total: 8, completed: 6, rate: 75.0 },
          { month: '2025-12', total: 7, completed: 6, rate: 85.7 },
          { month: '2026-01', total: 8, completed: 7, rate: 87.5 },
        ],
        factors: {
          overall_completion_rate: 77.4,
          baseline_rate: 62.5,
          latest_rate: 87.5,
          improvement: 25.0,
          trend: 'growing',
          trend_change: 12.3,
          total_sessions: 31,
          completed_sessions: 24,
          months_active: 4,
        },
      },
      {
        patient_id: 102,
        patient_name: 'Priya Patel',
        progress_score: 48.2,
        progress_level: 'Stable',
        monthly_progress: [
          { month: '2025-10', total: 6, completed: 4, rate: 66.7 },
          { month: '2025-11', total: 7, completed: 5, rate: 71.4 },
          { month: '2025-12', total: 6, completed: 4, rate: 66.7 },
          { month: '2026-01', total: 7, completed: 5, rate: 71.4 },
        ],
        factors: {
          overall_completion_rate: 69.2,
          baseline_rate: 66.7,
          latest_rate: 71.4,
          improvement: 4.7,
          trend: 'stable',
          trend_change: 1.2,
          total_sessions: 26,
          completed_sessions: 18,
          months_active: 4,
        },
      },
      {
        patient_id: 103,
        patient_name: 'Rohan Gupta',
        progress_score: 22.1,
        progress_level: 'Regression',
        monthly_progress: [
          { month: '2025-11', total: 5, completed: 4, rate: 80.0 },
          { month: '2025-12', total: 6, completed: 3, rate: 50.0 },
          { month: '2026-01', total: 4, completed: 2, rate: 50.0 },
        ],
        factors: {
          overall_completion_rate: 60.0,
          baseline_rate: 80.0,
          latest_rate: 50.0,
          improvement: -30.0,
          trend: 'declining',
          trend_change: -22.5,
          total_sessions: 15,
          completed_sessions: 9,
          months_active: 3,
        },
      },
      {
        patient_id: 104,
        patient_name: 'Ananya Singh',
        progress_score: 8.5,
        progress_level: 'Needs Attention',
        monthly_progress: [
          { month: '2026-01', total: 3, completed: 1, rate: 33.3 },
        ],
        factors: {
          overall_completion_rate: 33.3,
          baseline_rate: 33.3,
          latest_rate: 33.3,
          improvement: 0,
          trend: 'insufficient_data',
          trend_change: 0,
          total_sessions: 3,
          completed_sessions: 1,
          months_active: 1,
        },
      },
      {
        patient_id: 105,
        patient_name: 'Nisha Verma',
        progress_score: 0,
        progress_level: 'Limited Data',
        monthly_progress: [
          { month: '2026-02', total: 2, completed: 2, rate: 100.0 },
        ],
        factors: {
          overall_completion_rate: 0,
          trend: 'limited_data',
          trend_change: 0,
          total_sessions: 2,
          completed_sessions: 2,
        },
        eligibility: { eligible: false, maturity: 'limited_data', message: 'Progress scoring requires more session history.', reasons: ['2/3 sessions'] },
      },
    ],
    summary: {
      total: 4,
      improving: 1,
      stable: 1,
      regression: 1,
      needs_attention: 1,
      average_score: 37.8,
    },
  },
};

export const mockSinglePatientProgress = {
  rescode: 1,
  message: 'Success',
  data: {
    patient_id: 101,
    patient_name: 'Aryan Sharma',
    progress_score: 72.5,
    progress_level: 'Improving',
    monthly_progress: [
      { month: '2025-10', total: 8, completed: 5, rate: 62.5 },
      { month: '2025-11', total: 8, completed: 6, rate: 75.0 },
      { month: '2025-12', total: 7, completed: 6, rate: 85.7 },
      { month: '2026-01', total: 8, completed: 7, rate: 87.5 },
    ],
    factors: {
      overall_completion_rate: 77.4,
      baseline_rate: 62.5,
      latest_rate: 87.5,
      improvement: 25.0,
      trend: 'growing',
      trend_change: 12.3,
      total_sessions: 31,
      completed_sessions: 24,
      months_active: 4,
    },
  },
};

// ─── Therapy Effectiveness Index ─────────────────────────────────────────────

export const mockTherapyEffectiveness = {
  rescode: 1,
  message: 'Success',
  data: {
    therapies: [
      {
        therapy_id: 1,
        therapy_name: 'Speech Therapy',
        effectiveness_score: 78.4,
        effectiveness_tier: 'highly_effective',
        rank: 1,
        metrics: {
          total_sessions: 120,
          completed_sessions: 102,
          completion_rate: 85.0,
          unique_patients: 18,
          returning_patients: 14,
          retention_rate: 77.8,
          avg_sessions_per_patient: 6.7,
        },
      },
      {
        therapy_id: 2,
        therapy_name: 'Occupational Therapy',
        effectiveness_score: 65.2,
        effectiveness_tier: 'effective',
        rank: 2,
        metrics: {
          total_sessions: 85,
          completed_sessions: 68,
          completion_rate: 80.0,
          unique_patients: 14,
          returning_patients: 9,
          retention_rate: 64.3,
          avg_sessions_per_patient: 6.1,
        },
      },
      {
        therapy_id: 3,
        therapy_name: 'Behavioral Therapy',
        effectiveness_score: 52.8,
        effectiveness_tier: 'moderate',
        rank: 3,
        metrics: {
          total_sessions: 45,
          completed_sessions: 32,
          completion_rate: 71.1,
          unique_patients: 8,
          returning_patients: 4,
          retention_rate: 50.0,
          avg_sessions_per_patient: 5.6,
        },
      },
      {
        therapy_id: 4,
        therapy_name: 'Sensory Integration',
        effectiveness_score: 31.5,
        effectiveness_tier: 'needs_review',
        rank: 4,
        metrics: {
          total_sessions: 20,
          completed_sessions: 12,
          completion_rate: 60.0,
          unique_patients: 5,
          returning_patients: 2,
          retention_rate: 40.0,
          avg_sessions_per_patient: 4.0,
        },
      },
    ],
    summary: {
      total_therapies: 4,
      average_effectiveness: 57.0,
      top_therapy: 'Speech Therapy',
      tiers: {
        highly_effective: 1,
        effective: 1,
        moderate: 1,
        needs_review: 1,
      },
    },
  },
};

// ─── Goal Achievement Tracker ────────────────────────────────────────────────

export const mockGoalAchievement = {
  rescode: 1,
  message: 'Success',
  data: {
    patients: [
      {
        patient_id: 104,
        patient_name: 'Ananya Singh',
        goals: [
          { goal_id: 'attendance', goal_name: 'Attendance Target', description: 'Achieve >=80% session attendance rate', current_value: 33.3, target_value: 80, achieved: false, percentage: 41.6 },
          { goal_id: 'consistency', goal_name: 'Session Consistency', description: 'Average >=4 sessions per active month', current_value: 3.0, target_value: 4, achieved: false, percentage: 75.0 },
          { goal_id: 'retention', goal_name: 'Retention Milestone', description: 'Remain active for >=3 months', current_value: 1, target_value: 3, achieved: false, percentage: 33.3 },
          { goal_id: 'improvement', goal_name: 'Positive Progress Trend', description: 'Show improving completion rate over recent months', current_value: 'insufficient_data', target_value: 'growing', achieved: false, percentage: 0 },
        ],
        summary: { total_goals: 4, achieved: 0, overall_completion: 0, average_progress: 37.5, status: 'at_risk' },
      },
      {
        patient_id: 101,
        patient_name: 'Aryan Sharma',
        goals: [
          { goal_id: 'attendance', goal_name: 'Attendance Target', description: 'Achieve >=80% session attendance rate', current_value: 77.4, target_value: 80, achieved: false, percentage: 96.8 },
          { goal_id: 'consistency', goal_name: 'Session Consistency', description: 'Average >=4 sessions per active month', current_value: 7.8, target_value: 4, achieved: true, percentage: 100 },
          { goal_id: 'retention', goal_name: 'Retention Milestone', description: 'Remain active for >=3 months', current_value: 4, target_value: 3, achieved: true, percentage: 100 },
          { goal_id: 'improvement', goal_name: 'Positive Progress Trend', description: 'Show improving completion rate over recent months', current_value: 'growing', target_value: 'growing', achieved: true, percentage: 100 },
        ],
        summary: { total_goals: 4, achieved: 3, overall_completion: 75, average_progress: 99.2, status: 'on_track' },
      },
      {
        patient_id: 105,
        patient_name: 'Nisha Verma',
        goals: [
          { goal_id: 'attendance', goal_name: 'Attendance Target', description: 'Achieve >=80% session attendance rate', current_value: 0, target_value: 80, achieved: false, percentage: 0 },
          { goal_id: 'consistency', goal_name: 'Session Consistency', description: 'Average >=4 sessions per active month', current_value: 0, target_value: 4, achieved: false, percentage: 0 },
          { goal_id: 'retention', goal_name: 'Retention Milestone', description: 'Remain active for >=3 months', current_value: 0, target_value: 3, achieved: false, percentage: 0 },
          { goal_id: 'improvement', goal_name: 'Positive Progress Trend', description: 'Show improving completion rate over recent months', current_value: 0, target_value: 'growing', achieved: false, percentage: 0 },
        ],
        summary: { total_goals: 4, achieved: 0, overall_completion: 0, average_progress: 0, status: 'limited_data' },
        eligibility: { eligible: false, maturity: 'limited_data', message: 'Goal tracking requires more session history.', reasons: ['2/3 sessions'] },
      },
    ],
    summary: {
      total: 4,
      on_track: 1,
      partial: 1,
      at_risk: 1,
      off_track: 1,
      average_completion: 37.5,
    },
  },
};

export const mockSinglePatientGoals = {
  rescode: 1,
  message: 'Success',
  data: {
    patient_id: 101,
    patient_name: 'Aryan Sharma',
    goals: [
      { goal_id: 'attendance', goal_name: 'Attendance Target', description: 'Achieve >=80% session attendance rate', current_value: 77.4, target_value: 80, achieved: false, percentage: 96.8 },
      { goal_id: 'consistency', goal_name: 'Session Consistency', description: 'Average >=4 sessions per active month', current_value: 7.8, target_value: 4, achieved: true, percentage: 100 },
      { goal_id: 'retention', goal_name: 'Retention Milestone', description: 'Remain active for >=3 months', current_value: 4, target_value: 3, achieved: true, percentage: 100 },
      { goal_id: 'improvement', goal_name: 'Positive Progress Trend', description: 'Show improving completion rate over recent months', current_value: 'growing', target_value: 'growing', achieved: true, percentage: 100 },
    ],
    summary: { total_goals: 4, achieved: 3, overall_completion: 75, average_progress: 99.2, status: 'on_track' },
  },
};

// ─── Stagnation Detection ────────────────────────────────────────────────────

export const mockStagnationAlerts = {
  rescode: 1,
  message: 'Success',
  data: {
    alerts: [
      {
        patient_id: 103,
        patient_name: 'Rohan Gupta',
        stagnant: true,
        alert: {
          severity: 'medium',
          message: 'Patient "Rohan Gupta" has plateaued for 2 months. Monitor closely and consider adjusting treatment approach.',
          recommendation: [
            'Review session notes for patterns that may indicate underlying issues.',
            'Consider introducing supplementary activities or home exercises.',
          ],
          periods_flat: 2,
          current_rate: 50.0,
        },
        data: {
          months_tracked: 3,
          periods_flat: 2,
          last_change: 0,
          monthly_rates: [80.0, 50.0, 50.0],
          monthly_labels: ['2025-11', '2025-12', '2026-01'],
        },
      },
    ],
    all_patients: [
      { patient_id: 101, patient_name: 'Aryan Sharma', stagnant: false, alert: null, data: { months_tracked: 4, periods_flat: 0, last_change: 2.1, monthly_rates: [62.5, 75.0, 85.7, 87.5], monthly_labels: ['2025-10', '2025-11', '2025-12', '2026-01'] } },
      { patient_id: 102, patient_name: 'Priya Patel', stagnant: false, alert: null, data: { months_tracked: 4, periods_flat: 0, last_change: 7.0, monthly_rates: [66.7, 71.4, 66.7, 71.4], monthly_labels: ['2025-10', '2025-11', '2025-12', '2026-01'] } },
      { patient_id: 103, patient_name: 'Rohan Gupta', stagnant: true, alert: { severity: 'medium', message: 'Patient "Rohan Gupta" has plateaued for 2 months.', recommendation: ['Review session notes.', 'Consider supplementary activities.'], periods_flat: 2, current_rate: 50.0 }, data: { months_tracked: 3, periods_flat: 2, last_change: 0, monthly_rates: [80.0, 50.0, 50.0], monthly_labels: ['2025-11', '2025-12', '2026-01'] } },
      { patient_id: 104, patient_name: 'Ananya Singh', stagnant: false, alert: null, data: { months_tracked: 1, periods_flat: 0, last_change: 0, monthly_rates: [33.3], monthly_labels: ['2026-01'], reason: 'Insufficient data — need at least 3 months of history' }, eligibility: { eligible: false, maturity: 'limited_data', message: 'Stagnation detection requires at least 3 months of history.', reasons: ['1/3 months of data'] } },
    ],
    summary: {
      total_patients: 4,
      stagnant_patients: 1,
      critical: 0,
      high: 0,
      medium: 1,
      stagnation_rate: 25.0,
    },
  },
};

// ─── Clinical Recommendation Engine ──────────────────────────────────────────

export const mockClinicalRecommendations = {
  rescode: 1,
  message: 'Success',
  data: {
    patients: [
      {
        patient_id: 103,
        patient_name: 'Rohan Gupta',
        recommendations: [
          { category: 'attendance', priority: 'high', title: 'Below-Target Attendance', message: 'Attendance at 60% — below the 80% clinical target.', recommendation: 'Implement session reminders, consider rescheduling.', icon: 'warning' },
          { category: 'progress', priority: 'high', title: 'Declining Progress Trend', message: "Patient's completion rate is declining.", recommendation: 'Review therapy plan effectiveness.', icon: 'trending_down' },
          { category: 'stagnation', priority: 'medium', title: 'Stagnation Detected — 2 Months Flat', message: 'No meaningful improvement in 2 consecutive months.', recommendation: 'Introduce variety in therapy approach.', icon: 'horizontal_rule' },
        ],
        clinical_snapshot: { attendance_rate: 60.0, total_sessions: 15, completed_sessions: 9, months_active: 3, trend: 'declining', trend_change: -22.5, goals_achieved: 1, total_goals: 4, stagnation_detected: true },
        summary: { total_recommendations: 3, critical: 0, high: 2, medium: 1, info: 0 },
        generated_at: '2026-02-27T10:00:00.000Z',
      },
    ],
    alerts: [
      { patient_id: 103, patient_name: 'Rohan Gupta', category: 'attendance', priority: 'high', title: 'Below-Target Attendance', message: 'Attendance at 60%', recommendation: 'Implement session reminders.', icon: 'warning' },
      { patient_id: 103, patient_name: 'Rohan Gupta', category: 'progress', priority: 'high', title: 'Declining Progress Trend', message: 'Completion rate declining.', recommendation: 'Review therapy plan.', icon: 'trending_down' },
    ],
    summary: {
      total_patients: 4,
      total_alerts: 2,
      patients_with_critical: 0,
      patients_with_high: 1,
      patients_needing_attention: 1,
      category_breakdown: { attendance: 1, progress: 1, goals: 0, stagnation: 0 },
    },
    generated_at: '2026-02-27T10:00:00.000Z',
  },
};

export const mockSinglePatientRecommendations = {
  rescode: 1,
  message: 'Success',
  data: {
    patient_id: 101,
    patient_name: 'Aryan Sharma',
    recommendations: [
      { category: 'progress', priority: 'info', title: 'Positive Progress Trend', message: 'Patient is showing 12.3% average improvement. Current rate: 87.5%.', recommendation: 'Continue current therapy plan. Document progress milestones.', icon: 'trending_up' },
    ],
    clinical_snapshot: { attendance_rate: 77.4, total_sessions: 31, completed_sessions: 24, months_active: 4, trend: 'growing', trend_change: 12.3, goals_achieved: 3, total_goals: 4, stagnation_detected: false },
    summary: { total_recommendations: 1, critical: 0, high: 0, medium: 0, info: 1 },
    generated_at: '2026-02-27T10:00:00.000Z',
  },
};
